import { useState } from "react";
import { Link, useParams } from "react-router";
import type { RoutineDTO, RoutineProposalDTO, RoutineRunDTO } from "@roomate/core";
import { ApiError, useActors, useMe, useRoutine, useRoutineAction, useRoutineRuns, useRuns } from "../api.ts";
import { Action, Block, Card, Page, PageHeader, Pill } from "../calm.tsx";
import {
  canManageRoutines,
  durationsByRunId,
  capFraction,
  formatInZone,
  formatLocal,
  lastFinishedCost,
  newestFirst,
  RUN_STATUS_TONE,
  runNumbers,
  scheduleWords,
  STATUS_LABEL,
} from "../lib/routines.ts";
import { Ago, ErrorNote, formatSpan, Markdown } from "../ui.tsx";
import { money } from "./Run.tsx";

/*
 * One routine: how it is set up (read-only here), what it cost, and every run it made with the report,
 * the proposals and the links to the task and run (docs/specs/routines.md §3.2).
 */

export function RoutinePage() {
  const { id = "" } = useParams();
  const routine = useRoutine(id);
  const runs = useRoutineRuns(id);
  const actors = useActors().data;
  const me = useMe().data;
  const action = useRoutineAction(id);
  const canManage = canManageRoutines(me);
  // Duration is not on the routine run; it is joined from the agent's own runs by run id.
  const agentRuns = useRuns(`agentId=${routine.data?.agentId ?? ""}&limit=500`, !!routine.data);

  if (routine.error instanceof ApiError && routine.error.status === 404)
    return (
      <Page>
        <p className="text-ink-3">Routine not found.</p>
      </Page>
    );
  if (routine.error)
    return (
      <Page>
        <ErrorNote error={routine.error} />
      </Page>
    );
  if (!routine.data)
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );

  const r = routine.data;
  const agent = actors?.find((a) => a.id === r.agentId);
  const history = newestFirst(runs.data ?? []);
  const numbers = runNumbers(runs.data ?? []);
  const durations = durationsByRunId(agentRuns.data ?? []);
  const paused = r.status !== "active";

  return (
    <Page wide>
      <PageHeader
        crumbs={[{ label: "Routines", to: "/routines" }]}
        title={r.name}
        status={
          <Pill tone={r.status === "active" ? "live" : r.status === "paused_failing" ? "danger" : "neutral"} testId="routine-status">
            {STATUS_LABEL[r.status]}
          </Pill>
        }
        meta={
          <>
            <span data-testid="routine-schedule">{scheduleWords(r)}</span>
            <span className="text-ink-3">agent {agent?.name ?? r.agentId.slice(0, 10)}</span>
            {!paused && <span className="text-ink-3">next {formatInZone(r.nextDueAt, r.timezone)} ({formatLocal(r.nextDueAt)} your time)</span>}
          </>
        }
        actions={
          canManage ? (
            <>
              <Action
                primary
                testId="routine-run-now"
                disabled={action.isPending}
                onClick={() => action.mutate("run")}
              >
                {action.isPending && action.variables === "run" ? "Starting…" : "Run now"}
              </Action>
              <Action testId={paused ? "routine-resume" : "routine-pause"} disabled={action.isPending} onClick={() => action.mutate(paused ? "resume" : "pause")}>
                {paused ? "Resume" : "Pause"}
              </Action>
              <Link to={`/routines/${r.id}/edit`} data-testid="routine-edit" className="rounded-full bg-ink/[0.05] px-4 py-2 text-[13.5px] text-ink hover:bg-ink/[0.09]">
                Edit
              </Link>
            </>
          ) : undefined
        }
      />

      {action.error && (
        <div className="mt-4">
          <ErrorNote error={action.error} />
        </div>
      )}

      {r.status === "paused_failing" && <FailingNote routine={r} />}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-8">
          <Block title="Instructions" testId="routine-instructions">
            <Card>
              <p className="whitespace-pre-wrap break-words text-[14px] leading-relaxed text-ink-2">{r.instructions}</p>
            </Card>
          </Block>
          <Block title="Reads" testId="routine-reads">
            {r.reads.length === 0 ? (
              <p className="text-[13px] text-ink-3">Reads nothing.</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {r.reads.map((s) => (
                  <li key={s}>
                    <Pill>{s}</Pill>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </div>
        <CostPanel routine={r} lastCost={lastFinishedCost(history, r.lastRun)} />
      </div>

      <Block title="Run history" aside={`${history.length} run${history.length === 1 ? "" : "s"}, newest first`} className="mt-10" testId="routine-runs">
        {runs.error ? (
          <ErrorNote error={runs.error} />
        ) : !runs.data ? (
          <p className="text-[13px] text-ink-3">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-[13.5px] text-ink-3" data-testid="routine-runs-empty">
            No runs yet. The first one starts at the next scheduled time{canManage ? ", or press Run now" : ""}.
          </p>
        ) : (
          <ul className="divide-y divide-rule/60">
            {history.map((run) => (
              <RunRow key={run.id} run={run} number={numbers.get(run.id) ?? 0} duration={run.runId ? durations.get(run.runId) : undefined} timezone={r.timezone} />
            ))}
          </ul>
        )}
      </Block>
    </Page>
  );
}

function FailingNote({ routine }: { routine: RoutineDTO }) {
  return (
    <div role="status" data-testid="routine-failing" className="mt-6 rounded-2xl bg-high/10 px-5 py-4 text-[14px] text-high">
      <p className="font-medium">Paused after {routine.failureStreak} failures in a row. It will not run until someone resumes it.</p>
      <p className="mt-1 text-ink-2">
        {routine.pausedFailingDecisionId ? (
          <>
            A decision is waiting:{" "}
            <Link to={`/inbox?decision=${encodeURIComponent(routine.pausedFailingDecisionId)}`} data-testid="routine-failing-decision" className="underline">
              open the decision
            </Link>
            .
          </>
        ) : (
          "Resume it from the buttons above when the cause is fixed."
        )}
      </p>
    </div>
  );
}

function CostPanel({ routine: r, lastCost }: { routine: RoutineDTO; lastCost: number | null }) {
  const fraction = lastCost === null ? 0 : capFraction(lastCost, r.maxBudgetUsd);
  const over = lastCost !== null && lastCost >= r.maxBudgetUsd;
  return (
    <Block title="Cost" testId="routine-cost">
      <Card className="space-y-4">
        <div>
          <p className="text-[12.5px] text-ink-3">Last run against the cap</p>
          <p className="mt-1 text-[22px] font-semibold tabular-nums tracking-[-0.02em]" data-testid="routine-cost-last">
            {lastCost === null ? "no finished run yet" : money(lastCost)}
            <span className="ml-2 text-[14px] font-normal text-ink-3">of {money(r.maxBudgetUsd)} per run</span>
          </p>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink/[0.08]"
            role="meter"
            aria-label="Last run cost against the cap"
            aria-valuemin={0}
            aria-valuemax={r.maxBudgetUsd}
            aria-valuenow={Math.min(lastCost ?? 0, r.maxBudgetUsd)}
          >
            <div className={over ? "h-full bg-high" : "h-full bg-ink"} style={{ width: `${Math.round(fraction * 100)}%` }} />
          </div>
        </div>
        <div>
          <p className="text-[12.5px] text-ink-3">Last 30 days</p>
          <p className="mt-1 text-[22px] font-semibold tabular-nums tracking-[-0.02em]" data-testid="routine-cost-30d">
            {money(r.cost30dUsd)}
          </p>
        </div>
      </Card>
    </Block>
  );
}

function RunRow({ run, number, duration, timezone }: { run: RoutineRunDTO; number: number; duration: number | undefined; timezone: string }) {
  const [open, setOpen] = useState(false);
  const panelId = `routine-run-${run.id}`;
  return (
    <li data-testid="routine-run" data-run-status={run.status} className="py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid="routine-run-toggle"
        className="grid w-full grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-1 text-left text-[13.5px] md:grid-cols-[3rem_6rem_9rem_5rem_9rem_5rem_1fr]"
      >
        <span className="tabular-nums text-ink-3">#{number}</span>
        <span className="text-ink-2">{run.trigger}</span>
        <span className="text-ink-2">
          <Ago ts={run.createdAt} />
        </span>
        <span className="tabular-nums text-ink-3">{duration === undefined ? "no duration" : formatSpan(duration)}</span>
        <span>
          <Pill tone={RUN_STATUS_TONE[run.status] ?? "neutral"}>{run.status.replaceAll("_", " ")}</Pill>
        </span>
        <span className="tabular-nums text-ink-2">{money(run.costUsd)}</span>
        <span className="min-w-0 truncate text-ink-3">{run.summary ?? "no summary"}</span>
      </button>
      {open && (
        <div id={panelId} data-testid="routine-run-detail" className="mt-3 space-y-4 rounded-2xl bg-ink/[0.03] p-4">
          <p className="text-[12.5px] text-ink-3">
            Window {formatInZone(run.windowStart, timezone)} to {formatInZone(run.windowEnd, timezone)} {timezone}
          </p>
          {run.reportMd ? (
            <div data-testid="routine-run-report">
              <Markdown text={run.reportMd} headings />
            </div>
          ) : (
            <p className="text-[13px] text-ink-3" data-testid="routine-run-no-report">
              {run.status === "budget_exceeded" ? "No report: the run hit its budget cap." : "No report stored for this run."}
            </p>
          )}
          <Proposals proposals={run.proposals} />
          <p className="flex flex-wrap gap-x-4 text-[13px]">
            {run.taskId ? (
              <Link to={`/tasks/${run.taskId}`} className="text-ink-2 underline decoration-rule hover:text-ink" data-testid="routine-run-task">
                Open task
              </Link>
            ) : (
              <span className="text-ink-3">No task: the hub recorded this run itself</span>
            )}
            {run.runId && (
              <Link to={`/runs/${run.runId}`} className="text-ink-2 underline decoration-rule hover:text-ink" data-testid="routine-run-link">
                Open run
              </Link>
            )}
          </p>
        </div>
      )}
    </li>
  );
}

const PROPOSAL_TONE: Record<RoutineProposalDTO["state"], "signal" | "live" | "neutral"> = {
  open: "signal",
  created: "live",
  dismissed: "neutral",
  snoozed: "neutral",
};

function Proposals({ proposals }: { proposals: RoutineProposalDTO[] }) {
  return (
    <div data-testid="routine-run-proposals">
      <h3 className="mb-1.5 text-[13px] font-medium text-ink-2">Proposals</h3>
      {proposals.length === 0 ? (
        <p className="text-[13px] text-ink-3">No proposals.</p>
      ) : (
        <ul className="space-y-1.5">
          {proposals.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px]" data-testid="routine-proposal" data-proposal-state={p.state}>
              <Pill tone={PROPOSAL_TONE[p.state]}>{p.state}</Pill>
              <span className="min-w-0 break-words">{p.normalizedTitle}</span>
              {p.state === "snoozed" && p.snoozedUntil && <span className="text-[12px] text-ink-3">until {formatLocal(p.snoozedUntil)}</span>}
              <Link to={`/inbox?decision=${encodeURIComponent(p.decisionId)}`} className="text-[12.5px] text-ink-3 underline decoration-rule hover:text-ink">
                decision
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
